/* eslint-disable @typescript-eslint/no-explicit-any */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Typography from '@mui/material/Typography'
import { useConfirm } from 'material-ui-confirm'
import { useCallback, useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import * as Yup from 'yup'

import { createEventLog, updateParticipant } from 'api/mutations'
import { EVENT_LOG_ACTION_TYPES } from 'constants/models'
import { Diff } from 'constants/types'
import { Category, Event, LoggedInUser, Participant, Toast } from 'models/API'
import {
  generateInitFormParticipant,
  getParticipantDataDiff,
  isCategoryRelay,
} from 'utils/ApiHelpers'
import { doMutation } from 'utils/ApiUtils'

import Details from './Details'
import Form from './form'
import schema from './form/schema'

const getCategoryName = (categoryId: string, categories: Category[]) => {
  const category = categories.find((cat) => cat.id === categoryId)
  return category ? category.name : 'Unknown Category'
}

const Changes = ({
  diff,
  categories,
}: {
  diff: Diff
  categories: Category[]
}) => {
  const keys = Object.keys(diff.newData)

  if (keys.length === 0) {
    return <Typography>No changes detected</Typography>
  }

  return (
    <div>
      {keys.map((key) => {
        const oldValue =
          key === 'categoryId'
            ? getCategoryName(diff.oldData[key], categories)
            : diff.oldData[key] ?? <i>[NONE]</i>
        const newValue =
          key === 'categoryId'
            ? getCategoryName(diff.newData[key], categories)
            : diff.newData[key] ?? <i>[NONE]</i>

        return (
          <div key={key} style={{ marginBottom: '8px' }}>
            <Typography variant="body2">
              <strong>{key === 'categoryId' ? 'category' : key}:</strong>
            </Typography>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography
                variant="body2"
                style={{
                  flex: 1,
                  color: 'red',
                  wordBreak: 'break-word',
                }}>
                {oldValue}
              </Typography>
              <span style={{ flexShrink: 0 }}>→</span>
              <Typography
                variant="body2"
                style={{
                  flex: 1,
                  color: 'green',
                  wordBreak: 'break-word',
                }}>
                {newValue}
              </Typography>
            </div>
          </div>
        )
      })}
    </div>
  )
}

interface Props {
  data: Participant
  event: Event
  index: number
  loggedInUser: LoggedInUser
  toast: (toast: Toast) => void
}

const isRelay = (event: Event, categoryId: string) => {
  const category = (event.categories?.items as unknown as Category[])?.find(
    (c) => c.id === categoryId,
  )
  return isCategoryRelay(category)
}

const Item: React.FC<Props> = ({
  data,
  event,
  index,
  loggedInUser,
  toast,
}: Props) => {
  const confirm = useConfirm()
  const [participant, setParticipant] = useState(data)
  const [edit, setEdit] = useState(false)

  useEffect(() => {
    setParticipant(data)
  }, [data])

  const handleEditClick = useCallback(() => {
    setEdit(!edit)
  }, [edit])

  const handleSubmit = useCallback(
    async (values: Yup.InferType<typeof schema>) => {
      const categories = (event?.categories?.items ??
        []) as unknown as Category[]
      const category = categories?.filter(
        (c) => c.id === participant.categoryId,
      )?.[0]
      const oldData = generateInitFormParticipant(participant, event, category)

      const diff = getParticipantDataDiff(oldData, values)
      console.log({ oldData, values })

      const { confirmed } = await confirm({
        title: 'Changes to participant details',
        description: (
          <Changes
            diff={diff}
            categories={event?.categories?.items as unknown as Category[]}
          />
        ),
        confirmationText: 'Confirm',
        confirmationButtonProps: {
          variant: 'outlined',
          color: 'primary',
        },
        cancellationButtonProps: {
          variant: 'text',
          color: 'secondary',
        },
      })

      let newCategoryName = null
      const hasCategoryChange =
        'categoryId' in diff.newData &&
        diff.newData.categoryId !== participant.categoryId
      if (hasCategoryChange) {
        const newCategory = categories?.filter(
          (c) => c.id === diff.newData.categoryId,
        )?.[0]
        newCategoryName = newCategory ? newCategory.name : 'Unknown Category'
      }

      if (confirmed) {
        const eventLog = {
          id: uuidv4(),
          eventId: event.id,
          staffId: loggedInUser.sub,
          action: EVENT_LOG_ACTION_TYPES.UPDATE,
          description: `Updated participant details for ${participant.firstName} ${participant.lastName}`,
          newData: JSON.stringify({
            ...diff.newData,
            ...(hasCategoryChange && { categoryName: newCategoryName }),
          }),
          oldData: JSON.stringify({
            participant,
            oldData: {
              ...diff.oldData,
              ...(hasCategoryChange && { categoryName: category.name }),
            },
          }),
        }

        try {
          await doMutation('updateParticipant', updateParticipant, {
            input: {
              id: participant.id,
              ...diff.newData,
            },
          })

          await doMutation('createEventLog', createEventLog, {
            input: eventLog,
          })
          setParticipant({
            ...participant,
            ...diff.newData,
            ...(hasCategoryChange && {
              category: { id: diff.newData.categoryId, name: newCategoryName },
              categoryId: diff.newData.categoryId,
            }),
          })

          setEdit(false)
          toast({
            title: 'Success',
            message: 'Participant details updated successfully',
          })
        } catch (e) {
          console.log(e)
        }
      }

      // console.log(reason)
      //=> "confirm" | "cancel" | "natural" | "unmount"
    },
    [participant],
  )

  return (
    <Accordion>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`panel${index}-content`}
        id={`panel${index}-header`}>
        <Typography component="span" sx={{ mr: 1 }}>
          {participant.firstName} {participant.lastName}
        </Typography>
        <Typography sx={{ color: 'grey' }}>
          {participant.category.name}
          {isRelay(event, participant.categoryId)
            ? ` - ${participant.relayEvent}`
            : ''}
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        {edit ? (
          <Form
            onSubmit={handleSubmit}
            onCancel={handleEditClick}
            participant={participant}
            event={event}
          />
        ) : (
          <Details
            participant={participant}
            event={event}
            onClick={handleEditClick}
          />
        )}
      </AccordionDetails>
    </Accordion>
  )
}

export default Item
